import { useState } from "react"
import { useQuery } from "@tanstack/react-query"
import { Info, MinusCircle, PlusCircle, PenLine } from "lucide-react"
import { PageHeader } from "@/components/portal/PageHeader"
import { ErrorBox } from "@/components/portal/LoadingError"
import { DatenstandHinweis } from "@/components/portal/DatenstandHinweis"

/**
 * „Vorschlagsqualität" — wo weicht das Büro vom vorgeschlagenen Plan ab? (#64)
 *
 * Paul am 02.09.2026: „Sie soll das klar loggen die aktivitäten aber dann auch
 * einfließen lassen in eine Modellverbesserung/Prompt verbesserung … Wenn wir
 * vom vorgeschlagenen plan zb abweichen."
 *
 * Der Assistent schlägt einen Plan vor, der Mensch korrigiert ihn vor dem
 * Ausführen. Diese Korrektur ist die wertvollste Information im System — sie
 * sagt exakt, wo der Vorschlag falsch lag. Hier wird sie gezählt.
 *
 * 🔑 Bewusst getrennt von „Wege": dort zählt Bedienung, hier die Qualität der
 * Vorschläge. Und bewusst eine Zählung, keine automatische Prompt-Änderung —
 * das System liefert Belege, der Mensch entscheidet.
 *
 * 🔴 Ohne Volltexte. `mass` sagt „zu 80 % umgeschrieben", nicht was.
 */

type Zeile = {
  tenant_slug: string
  aktion: string
  art: "abgewaehlt" | "hinzugefuegt" | "geaendert" | "verworfen" | "abgelaufen" | "weitergeleitet" | "erledigt" | "nichts_noetig" | "anders_erledigt"
  feld: string | null
  anzahl: number
  mails: number
  mass_mittel: number
}
type Kennzahlen = {
  abweichungen?: number; mails_mit_abweichung?: number; benutzer?: number
  abgewaehlt?: number; hinzugefuegt?: number; geaendert?: number
  plaene_ausgefuehrt?: number
  /** #80 „no touch": unveraendert ausgefuehrte Plaene — die KI-Quote. */
  no_touch?: number; no_touch_basis?: number; no_touch_quote?: number | null
  je_szenario?: { szenario: string; ausgefuehrt: number; no_touch: number }[]
  /** #64 Teil 0: wie endeten die Plaene — der ehrliche Nenner. */
  ausgaenge?: { je_ausgang: Record<string, number>; plaene_gesamt: number }
  /** Paul, 16.09.2026: erkennt der Planer den Kunden — und haengt das Buero einen anderen dran? */
  kontakt_erkennung?: {
    plaene: number; erkannt: number; bestaetigt: number; anders_verknuepft: number
    erkannt_nicht_verknuepft: number; nicht_erkannt_verknuepft: number; ohne_kontakt: number
    trefferquote: number | null
    nachschau_quellen: { quelle: string; anzahl: number }[]
  }
}

const NACHSCHAU_QUELLE: Record<string, string> = {
  mail: "Verknüpfung an der Mail", plan: "vom Planer erkannt", absender: "Absenderadresse",
  thread: "andere Mail im Gespräch", telefon: "Rufnummer aus der Signatur", firma: "Firmen-Domain (vermutlich)",
  name: "Vor- und Nachname (vermutlich)", keiner: "kein Kunde",
}

const AUSGANG: { key: string; label: string; hinweis: string }[] = [
  { key: "ausgefuehrt", label: "ausgeführt", hinweis: "alle Schritte wie vorgeschlagen" },
  { key: "teilweise", label: "teilweise", hinweis: "mindestens ein Schritt abgewählt" },
  { key: "beantwortet", label: "beantwortet", hinweis: "Antwort gesendet, ggf. umgeschrieben" },
  { key: "weitergeleitet", label: "weitergeleitet", hinweis: "statt des Plans weitergeleitet" },
  { key: "verworfen", label: "verworfen", hinweis: "„Ohne Antwort erledigt“ oder „Verwerfen“" },
  { key: "erledigt", label: "von Hand erledigt", hinweis: "Status auf erledigt, ohne Plan und Versand" },
  { key: "abgelaufen", label: "liegen geblieben", hinweis: "sieben Tage ohne jede Aktion" },
  { key: "anders_erledigt", label: "anders erledigt", hinweis: "Nachschau fand Rückruf, Mail oder HERO-Eintrag — von selbst geschlossen" },
  { key: "nichts_noetig", label: "nichts nötig", hinweis: "Plan „nur zur Kenntnis“, nach dem Ansehen von selbst geschlossen" },
]

const SZENARIO: Record<string, string> = {
  neukunde_termin: "Neukunden-Termin",
  bestandskunde_termin: "Termin-Buchung",
  reply_only: "Antwort / Aufgabe",
  skip: "Keine Aktion",
}
type Antwort = { kennzahlen: Kennzahlen; auswertung: Zeile[]; belastbar: boolean
  /** #101: ältester zugrunde liegender Eintrag (je Mandant) — reicht der Zeitraum weiter zurück, sagt die Seite es. */
  daten_ab?: string | null }

const lesbar = (s: string) => s.replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase())

const ART = {
  abgewaehlt: { label: "abgewählt", icon: MinusCircle, farbe: "#b45309", hinweis: "vorgeschlagen, aber nicht ausgeführt" },
  hinzugefuegt: { label: "hinzugefügt", icon: PlusCircle, farbe: "#047857", hinweis: "ausgeführt, aber nicht vorgeschlagen" },
  geaendert: { label: "geändert", icon: PenLine, farbe: "#1d4ed8", hinweis: "ausgeführt, aber vorher umgeschrieben" },
  // #64 Teil 0: der ganze Plan als Abweichung (aktion = "plan")
  verworfen: { label: "verworfen", icon: MinusCircle, farbe: "#b91c1c", hinweis: "Plan ohne Antwort erledigt oder verworfen" },
  abgelaufen: { label: "liegen gelassen", icon: MinusCircle, farbe: "#6b7280", hinweis: "sieben Tage ohne jede Aktion" },
  weitergeleitet: { label: "weitergeleitet", icon: MinusCircle, farbe: "#b45309", hinweis: "statt des Plans weitergeleitet" },
  erledigt: { label: "von Hand erledigt", icon: MinusCircle, farbe: "#6b7280", hinweis: "Status auf erledigt, ohne Plan und Versand" },
  anders_erledigt: { label: "anders erledigt", icon: MinusCircle, farbe: "#047857", hinweis: "Rückruf, Mail oder HERO-Eintrag gefunden, Plan von selbst geschlossen" },
} as const

function Satz({ z }: { z: Zeile }) {
  // Ein Satz statt einer Tabellenzeile — so liest sich der Befund, wie Paul
  // ihn braucht: „Rechnung an Buchhaltung: 14× vorgeschlagen, 12× abgewählt".
  const feld = z.feld ? ` (${z.feld.split(".").pop()})` : ""
  if (z.art === "geaendert") {
    return (
      <>
        <strong>{lesbar(z.aktion)}{feld}</strong> wurde in {z.mails} {z.mails === 1 ? "Mail" : "Mails"} umgeschrieben
        {z.mass_mittel > 0 && <> — im Mittel zu <strong>{Math.round(z.mass_mittel * 100)} %</strong></>}
      </>
    )
  }
  return (
    <>
      <strong>{z.aktion === "plan" ? "Der ganze Plan" : lesbar(z.aktion)}</strong> wurde {z.anzahl}× {(ART[z.art] ?? { label: z.art }).label}
      {z.mails !== z.anzahl && <> ({z.mails} Mails)</>}
    </>
  )
}

export default function VorschlagsqualitaetPage() {
  const [tage, setTage] = useState(30)
  const [tenant, setTenant] = useState("")

  const { data, isLoading, error, refetch, isFetching } = useQuery<Antwort>({
    queryKey: ["vorschlagsqualitaet", tage, tenant],
    queryFn: async () => {
      const p = new URLSearchParams({ tage: String(tage) })
      if (tenant) p.set("tenant", tenant)
      const r = await fetch(`/api/v1/vorschlagsqualitaet?${p}`, { credentials: "include" })
      if (!r.ok) throw new Error(`HTTP ${r.status}`)
      return r.json()
    },
  })

  if (error) return <div className="p-10"><ErrorBox error={error} /></div>

  const k = data?.kennzahlen || {}
  const zeilen = data?.auswertung || []
  const tenants = Array.from(new Set(zeilen.map((z) => z.tenant_slug).filter(Boolean))).sort()

  return (
    <div className="px-10 py-8 max-w-[1100px]">
      <PageHeader title="Vorschlagsqualität" onRefresh={() => refetch()} refreshing={isFetching} />

      <p className="text-[13px] text-muted-foreground mb-4 max-w-[72ch]">
        Wo das Büro den vorgeschlagenen Plan vor dem Ausführen korrigiert hat —
        abgewählt, ergänzt, umgeschrieben. Gezählt wird die Abweichung, nicht der
        Inhalt: kein Betreff, kein Kundenname, kein Text. Daraus lässt sich lesen,
        welche Regel im Prompt regelmäßig danebenliegt.
      </p>

      <div className="flex items-center gap-3 mb-4 text-[12.5px]">
        <label className="inline-flex items-center gap-1.5">
          Zeitraum
          <select className="border rounded px-2 py-1 bg-transparent" value={tage}
                  onChange={(e) => setTage(Number(e.target.value))}>
            <option value={7}>7 Tage</option>
            <option value={30}>30 Tage</option>
            <option value={90}>90 Tage</option>
          </select>
        </label>
        {tenants.length > 1 && (
          <label className="inline-flex items-center gap-1.5">
            Mandant
            <select className="border rounded px-2 py-1 bg-transparent" value={tenant}
                    onChange={(e) => setTenant(e.target.value)}>
              <option value="">alle</option>
              {tenants.map((t) => <option key={t} value={t}>{t}</option>)}
            </select>
          </label>
        )}
      </div>

      <DatenstandHinweis datenAb={data?.daten_ab} tage={tage} />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-5">
        {[
          ["Pläne ausgeführt", k.plaene_ausgefuehrt],
          ["davon mit Abweichung", k.mails_mit_abweichung],
          ["Abweichungen", k.abweichungen],
          ["Personen", k.benutzer],
        ].map(([l, v]) => (
          <div key={String(l)} className="pp-card p-3">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{l}</div>
            <div className="text-[22px] font-semibold">{(v as number | undefined) ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* #64 Teil 0 (11.09.2026): wie endeten die Pläne? Bis hier zählte nur
          „ausgeführt" — verworfene und liegen gebliebene Pläne fehlten im
          Nenner, also genau die, bei denen der Vorschlag am weitesten
          daneben lag. */}
      {(k.ausgaenge?.plaene_gesamt ?? 0) > 0 && (
        <div className="pp-card p-3 mb-5">
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Wie die Pläne endeten</div>
            <div className="text-[12.5px] text-muted-foreground">{k.ausgaenge?.plaene_gesamt} Pläne mit Ausgang im Zeitraum</div>
          </div>
          <ul className="mt-2 text-[12.5px] grid grid-cols-2 md:grid-cols-4 gap-1">
            {AUSGANG.map((a) => (
              <li key={a.key} title={a.hinweis}>
                <strong>{a.label}</strong>: {k.ausgaenge?.je_ausgang?.[a.key] ?? 0}
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* Paul, 16.09.2026: „eig erkennt der planner den kontakt doch automatisch
          oder? das würde ich auch gerne sehen, wie oft dann ein anderer kontakt
          verknüpft wird." Verglichen wird der Kontakt im Plan mit der späteren
          Verknüpfung an der Mail. */}
      {(k.kontakt_erkennung?.plaene ?? 0) > 0 && (() => {
        const ke = k.kontakt_erkennung!
        return (
          <div className="pp-card p-3 mb-5">
            <div className="flex items-baseline gap-3 flex-wrap">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Kontakt-Erkennung des Planers</div>
              <div className="text-[22px] font-semibold">{ke.trefferquote ?? "—"} %</div>
              <div className="text-[12.5px] text-muted-foreground">
                {ke.bestaetigt} von {ke.bestaetigt + ke.anders_verknuepft} vom Büro verknüpften Fällen stimmten mit dem Planer überein · {ke.erkannt} von {ke.plaene} Plänen mit erkanntem Kunden
              </div>
            </div>
            <ul className="mt-2 text-[12.5px] grid grid-cols-2 md:grid-cols-3 gap-1">
              <li title="Planer und Büro nennen denselben Kontakt"><strong>bestätigt</strong>: {ke.bestaetigt}</li>
              <li title="Das Büro hat einen anderen Kontakt verknüpft als der Planer fand"><strong>anders verknüpft</strong>: {ke.anders_verknuepft}</li>
              <li title="Planer fand einen Kontakt, das Büro hat nie verknüpft — die Nachschau nimmt den des Planers"><strong>erkannt, nicht verknüpft</strong>: {ke.erkannt_nicht_verknuepft}</li>
              <li title="Planer fand nichts, das Büro hat verknüpft — hier fehlt dem Planer ein Schlüssel"><strong>nicht erkannt, aber verknüpft</strong>: {ke.nicht_erkannt_verknuepft}</li>
              <li title="Weder Planer noch Büro — Lieferanten, Newsletter, unbekannte Absender"><strong>ohne Kontakt</strong>: {ke.ohne_kontakt}</li>
            </ul>
            {ke.nachschau_quellen?.length > 0 && (
              <div className="mt-2 text-[12px] text-muted-foreground">
                Woher die Nachschau ihren Kunden hatte:{" "}
                {ke.nachschau_quellen.map((q) => `${NACHSCHAU_QUELLE[q.quelle] ?? q.quelle} ${q.anzahl}`).join(" · ")}
              </div>
            )}
          </div>
        )
      })()}

      {/* #80 (Marlene, 07.09.2026): „wir brauchen unsere KI-Quote — immer wenn
          z.B. ein Neukunden-Termin perfekt angelegt wurde, sollte das gezählt
          werden". Basis sind ausgeführte Pläne; ohne Eintrag bei den
          Abweichungen gilt der Plan als unverändert durchgegangen. */}
      {(k.no_touch_basis ?? 0) > 0 && (
        <div className="pp-card p-3 mb-5">
          <div className="flex items-baseline gap-3 flex-wrap">
            <div className="text-[11px] uppercase tracking-wide text-muted-foreground">KI-Quote („no touch“)</div>
            <div className="text-[22px] font-semibold">{k.no_touch_quote ?? "—"} %</div>
            <div className="text-[12.5px] text-muted-foreground">
              {k.no_touch} von {k.no_touch_basis} ausgeführten oder gesendeten Plänen gingen unverändert durch — ohne Pläne „Keine Aktion“
            </div>
          </div>
          <ul className="mt-2 text-[12.5px] grid grid-cols-2 md:grid-cols-4 gap-1">
            {(k.je_szenario ?? []).map((s) => (
              <li key={s.szenario}>
                <strong>{SZENARIO[s.szenario] ?? s.szenario}</strong>: {s.no_touch} / {s.ausgefuehrt}
              </li>
            ))}
          </ul>
        </div>
      )}

      {data && !data.belastbar && (
        <div className="flex gap-2 items-start rounded-md border border-amber-300 bg-amber-50 text-amber-900 px-3 py-2 mb-4 text-[12.5px]">
          <Info className="w-4 h-4 mt-0.5 shrink-0" />
          <div>
            <strong>Noch zu wenig Daten für belastbare Aussagen.</strong> Bisher
            wurden {k.plaene_ausgefuehrt ?? 0} Pläne ausgeführt; ab etwa 20 wird
            aus Zufall ein Muster. Was unten steht, ist eine Momentaufnahme —
            keine Empfehlung, den Prompt zu ändern.
          </div>
        </div>
      )}

      {isLoading ? (
        <div className="text-[13px] text-muted-foreground">Lädt…</div>
      ) : zeilen.length === 0 ? (
        <div className="pp-card p-6 text-center text-[13px] text-muted-foreground">
          Noch keine Abweichungen erfasst. Sie entstehen, sobald jemand einen
          Plan vor dem Ausführen korrigiert.
        </div>
      ) : (
        <div className="pp-card divide-y">
          {zeilen.map((z, i) => {
            const A = ART[z.art]
            return (
              <div key={i} className="flex items-center gap-3 px-4 py-2.5 text-[13px]">
                <span title={A.hinweis} className="shrink-0 inline-flex">
                  <A.icon className="w-4 h-4" style={{ color: A.farbe }} />
                </span>
                <div className="flex-1 min-w-0"><Satz z={z} /></div>
                {!tenant && z.tenant_slug && (
                  <span className="text-[11px] text-muted-foreground shrink-0">{z.tenant_slug}</span>
                )}
                <span className="text-[12px] font-medium tabular-nums shrink-0" style={{ color: A.farbe }}>
                  {z.anzahl}×
                </span>
              </div>
            )
          })}
        </div>
      )}

      <p className="text-[12px] text-muted-foreground mt-4 max-w-[72ch]">
        Der Vorschlag wird beim ersten Bearbeiten gesichert und beim Ausführen mit
        dem verglichen, was tatsächlich lief. Was hier häufig auftaucht, ist ein
        Kandidat für eine Prompt- oder Tool-Änderung — entscheiden tut das ein
        Mensch, nicht diese Seite.
      </p>
    </div>
  )
}
